import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { supabase } from '@/shared/lib/supabase'

export interface Notification {
  id: string
  type: string
  title: string
  body: string | null
  team_id: string | null
  project_id: string | null
  feature_id: string | null
  user_story_id: string | null
  is_read: boolean
  created_at: string
  team_short_id: string | null
  project_short_id: string | null
  feature_short_id: string | null
  user_story_short_id: string | null
}

async function fetchNotifications(limit = 20, offset = 0): Promise<Notification[]> {
  const { data, error } = await supabase.rpc('get_notifications', {
    p_limit: limit,
    p_offset: offset,
    p_unread_only: false,
  })

  if (error) {
    console.error('Error fetching notifications:', error)
    return []
  }

  return data as Notification[]
}

async function fetchUnreadCount(): Promise<number> {
  const { data, error } = await supabase.rpc('get_unread_notification_count')

  if (error) {
    console.error('Error fetching unread count:', error)
    return 0
  }

  return data as number
}

async function markAsRead(notificationId: string): Promise<void> {
  const { error } = await supabase.rpc('mark_notification_read', {
    p_notification_id: notificationId,
  })

  if (error) {
    console.error('Error marking notification as read:', error)
  }
}

async function markAllAsRead(): Promise<void> {
  const { error } = await supabase.rpc('mark_all_notifications_read')

  if (error) {
    console.error('Error marking all notifications as read:', error)
  }
}

export function useNotifications(limit = 20) {
  return useQuery({
    queryKey: ['notifications', limit],
    queryFn: () => fetchNotifications(limit),
  })
}

export function useUnreadNotificationCount() {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['unreadNotificationCount'],
    queryFn: fetchUnreadCount,
    refetchInterval: 30000, // Refetch every 30 seconds as backup
  })

  // Subscribe to realtime notifications
  useEffect(() => {
    const setupSubscription = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser()

      if (!user) return

      const channel = supabase
        .channel('notifications-realtime')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${user.id}`,
          },
          () => {
            // Invalidate queries when new notification arrives
            queryClient.invalidateQueries({ queryKey: ['unreadNotificationCount'] })
            queryClient.invalidateQueries({ queryKey: ['notifications'] })
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${user.id}`,
          },
          () => {
            // Invalidate on read status change
            queryClient.invalidateQueries({ queryKey: ['unreadNotificationCount'] })
            queryClient.invalidateQueries({ queryKey: ['notifications'] })
          }
        )
        .subscribe()

      return () => {
        supabase.removeChannel(channel)
      }
    }

    const cleanup = setupSubscription()
    return () => {
      cleanup.then((fn) => fn?.())
    }
  }, [queryClient])

  return query
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['unreadNotificationCount'] })
    },
  })
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: markAllAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['unreadNotificationCount'] })
    },
  })
}

// Build the navigation URL for a notification
// Routes: /us/:shortId, /f/:shortId, /p/:shortId, /t/:shortId/exchange
export function getNotificationUrl(notification: Notification): string {
  // specs_ready → post-scoping page with PDF download
  if (notification.type === 'specs_ready' && notification.project_short_id) {
    return `/scoping/${notification.project_short_id}/complete`
  }

  // US notifications -> direct US page
  if (notification.user_story_short_id) {
    return `/us/${notification.user_story_short_id}`
  }

  // Feature notifications -> direct feature page
  if (notification.feature_short_id) {
    return `/f/${notification.feature_short_id}`
  }

  // Project notifications -> project kanban page
  if (notification.project_short_id) {
    return `/p/${notification.project_short_id}`
  }

  // Team-level notifications (new_message at team level) -> exchange zone
  if (notification.team_short_id) {
    return `/t/${notification.team_short_id}/exchange`
  }

  return '/dashboard'
}
